import { apiClient } from './client';

export async function login(email, password, deviceName = 'mafert-system-web') {
  const { data } = await apiClient.post('/api/auth/login', {
    email,
    password,
    device_name: deviceName,
  });

  return data;
}

export async function fetchMe() {
  const { data } = await apiClient.get('/api/auth/me');

  return data;
}

export async function logout() {
  await apiClient.post('/api/auth/logout');
}

export async function completeTwoFactorChallenge(payload) {
  const { data } = await apiClient.post('/api/auth/2fa/challenge', payload);
  return data;
}

export async function setupTwoFactor() {
  const { data } = await apiClient.post('/api/auth/2fa/setup');
  return data.data;
}

export async function confirmTwoFactor(code) {
  const { data } = await apiClient.post('/api/auth/2fa/confirm', { code });
  return data;
}

export async function disableTwoFactor(currentPassword) {
  const { data } = await apiClient.post('/api/auth/2fa/disable', {
    current_password: currentPassword,
  });
  return data;
}

export async function regenerateRecoveryCodes(currentPassword) {
  const { data } = await apiClient.post('/api/auth/2fa/recovery-codes/regenerate', {
    current_password: currentPassword,
  });
  return data;
}

export async function changePassword(payload) {
  const { data } = await apiClient.post('/api/auth/password/change', payload);
  return data;
}

export async function forgotPassword(email) {
  const { data } = await apiClient.post('/api/auth/password/forgot', { email });
  return data;
}

export async function resetPassword(payload) {
  const { data } = await apiClient.post('/api/auth/password/reset', payload);
  return data;
}

export async function fetchSessions() {
  const { data } = await apiClient.get('/api/auth/sessions');
  return data.data || [];
}

export async function revokeSession(tokenId) {
  await apiClient.delete(`/api/auth/sessions/${tokenId}`);
}

export async function logoutOtherSessions() {
  const { data } = await apiClient.post('/api/auth/sessions/logout-others');
  return data;
}

export async function fetchSecurityEvents(params = {}) {
  const { data } = await apiClient.get('/api/auth/security-events', { params });
  return data;
}

export async function fetchCustomers(params = {}) {
  const { data } = await apiClient.get('/api/system/customers', {
    params: { per_page: 50, ...params },
  });

  return data;
}

export async function getCustomer(id) {
  const { data } = await apiClient.get(`/api/system/customers/${id}`);

  return data.data;
}

export async function fetchTenants(params = {}) {
  const { data } = await apiClient.get('/api/system/tenants', {
    params: { per_page: 50, ...params },
  });

  return data;
}

export async function getTenant(id) {
  const { data } = await apiClient.get(`/api/system/tenants/${id}`);

  return data.data;
}

export async function updateTenant(id, payload) {
  const { data } = await apiClient.put(`/api/system/tenants/${id}`, payload);

  return data.data;
}

export async function getTenantEvents(params = {}) {
  const { data } = await apiClient.get('/api/system/tenant-events', {
    params: { per_page: 10, ...params },
  });

  return data;
}

export async function fetchSupportTickets(params = {}) {
  const { data } = await apiClient.get('/api/system/support/tickets', {
    params: { per_page: 25, ...params },
  });

  return data;
}

export async function fetchSupportAssignees() {
  const { data } = await apiClient.get('/api/system/support/assignees');

  return data.data || [];
}

/**
 * Customer-scoped requesters for System create-on-behalf.
 * @param {number|string} customerId
 * @param {{ q?: string }} [params]
 */
export async function fetchSupportRequesters(customerId, params = {}) {
  const { data } = await apiClient.get('/api/system/support/requesters', {
    params: { customer_id: customerId, ...params },
  });

  return data.data || [];
}

export async function getSupportTicket(id) {
  const { data } = await apiClient.get(`/api/system/support/tickets/${id}`);

  return data.data;
}

export async function createSupportTicket(payload) {
  const { data } = await apiClient.post('/api/system/support/tickets', payload);

  return data.data;
}

export async function updateSupportTicket(id, payload) {
  const { data } = await apiClient.put(`/api/system/support/tickets/${id}`, payload);

  return data.data;
}

export async function assignSupportTicket(id, payload) {
  const { data } = await apiClient.patch(`/api/system/support/tickets/${id}/assign`, payload);

  return data.data;
}

export async function updateSupportTicketStatus(id, payload) {
  const { data } = await apiClient.patch(`/api/system/support/tickets/${id}/status`, payload);

  return data.data;
}

export async function createSupportMessage(id, payload) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${id}/messages`, payload);

  return data.data;
}

export async function createInternalNote(id, payload) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${id}/internal-notes`, payload);

  return data.data;
}

export async function getSupportTicketEvents(id, params = {}) {
  const { data } = await apiClient.get(`/api/system/support/tickets/${id}/events`, {
    params: { per_page: 20, ...params },
  });

  return data;
}

export async function markSupportTicketViewed(id) {
  await apiClient.post(`/api/system/support/tickets/${id}/mark-as-viewed`);
}

export async function markSupportTicketUnread(id) {
  await apiClient.post(`/api/system/support/tickets/${id}/mark-unread`);
}

export async function uploadSupportAttachment(ticketId, formData) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${ticketId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data.attachment || data.data;
}

export async function downloadSupportAttachment(ticketId, attachmentId) {
  const response = await apiClient.get(
    `/api/system/support/tickets/${ticketId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' },
  );
  return response.data;
}

export async function deleteSupportAttachment(ticketId, attachmentId) {
  await apiClient.delete(`/api/system/support/tickets/${ticketId}/attachments/${attachmentId}`);
}

export async function getSupportWatchers(ticketId) {
  const { data } = await apiClient.get(`/api/system/support/tickets/${ticketId}/watchers`);
  return data;
}

export async function addSupportWatcher(ticketId, payload) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${ticketId}/watchers`, payload);
  return data;
}

export async function removeSupportWatcher(ticketId, userId) {
  await apiClient.delete(`/api/system/support/tickets/${ticketId}/watchers/${userId}`);
}

export async function muteSupportWatcher(ticketId, userId) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${ticketId}/watchers/${userId}/mute`);
  return data;
}

export async function updateSupportTicketNotification(ticketId, payload) {
  const { data } = await apiClient.patch(`/api/system/support/tickets/${ticketId}/notification`, payload);
  return data.data;
}

export async function mentionSupportUsers(ticketId, payload) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${ticketId}/mention`, payload);
  return data;
}

export async function getSupportSla(ticketId) {
  const { data } = await apiClient.get(`/api/system/support/tickets/${ticketId}/sla`);
  return data.data;
}

export async function recalculateSupportSla(ticketId) {
  const { data } = await apiClient.post(`/api/system/support/tickets/${ticketId}/recalculate-sla`);
  return data.data;
}

export async function exportSupportTickets(params = {}) {
  const response = await apiClient.get('/api/system/support/exports/tickets', {
    params: { format: 'csv', ...params },
    responseType: 'blob',
  });
  return response.data;
}

export async function fetchSupportNotificationSettings() {
  const { data } = await apiClient.get('/api/system/support/notification-settings');
  return data.data;
}

export async function updateSupportNotificationSettings(payload) {
  const { data } = await apiClient.put('/api/system/support/notification-settings', payload);
  return data.data;
}

export async function fetchPlans(params = {}) {
  const { data } = await apiClient.get('/api/system/plans', { params: { per_page: 50, ...params } });
  return data;
}

export async function createPlan(payload) {
  const { data } = await apiClient.post('/api/system/plans', payload);
  return data.data;
}

export async function updatePlan(id, payload) {
  const { data } = await apiClient.put(`/api/system/plans/${id}`, payload);
  return data.data;
}

export async function activatePlan(id) {
  const { data } = await apiClient.patch(`/api/system/plans/${id}/activate`);
  return data.data;
}

export async function deactivatePlan(id) {
  const { data } = await apiClient.patch(`/api/system/plans/${id}/deactivate`);
  return data.data;
}

export async function fetchSubscriptions(params = {}) {
  const { data } = await apiClient.get('/api/system/subscriptions', {
    params: { per_page: 50, ...params },
  });
  return data;
}

export async function createSubscription(payload) {
  const { data } = await apiClient.post('/api/system/subscriptions', payload);
  return data.data;
}

export async function updateSubscriptionStatus(id, payload) {
  const { data } = await apiClient.patch(`/api/system/subscriptions/${id}/status`, payload);
  return data.data;
}

export async function cancelSubscription(id, payload = {}) {
  const { data } = await apiClient.patch(`/api/system/subscriptions/${id}/cancel`, payload);
  return data.data;
}

export async function blockSubscriptionPayment(id, payload = {}) {
  const { data } = await apiClient.patch(`/api/system/subscriptions/${id}/block-payment`, payload);
  return data.data;
}

export async function unblockSubscriptionPayment(id) {
  const { data } = await apiClient.patch(`/api/system/subscriptions/${id}/unblock-payment`);
  return data.data;
}

export async function getSubscriptionAccessStatus(id) {
  const { data } = await apiClient.get(`/api/system/subscriptions/${id}/access-status`);
  return data;
}

export async function fetchPayments(params = {}) {
  const { data } = await apiClient.get('/api/system/payments', { params: { per_page: 50, ...params } });
  return data;
}

export async function createPayment(payload) {
  const { data } = await apiClient.post('/api/system/payments', payload);
  return data.data;
}

export async function markPaymentPaid(id, payload = {}) {
  const { data } = await apiClient.patch(`/api/system/payments/${id}/mark-paid`, payload);
  return data.data;
}

export async function markPaymentFailed(id) {
  const { data } = await apiClient.patch(`/api/system/payments/${id}/mark-failed`);
  return data.data;
}

export async function previewTenantSlug(params = {}) {
  const { data } = await apiClient.get('/api/system/onboarding/preview-slug', { params });
  return data.data;
}

export async function createTrialTenant(payload) {
  const { data } = await apiClient.post('/api/system/onboarding/trial-tenant', payload);
  return data.data;
}

export async function getTenantReadiness(tenantId) {
  const { data } = await apiClient.get(`/api/system/tenants/${tenantId}/readiness`);
  return data.data;
}

export async function getTenantReadinessSummary(params = {}) {
  const { data } = await apiClient.get('/api/system/provisioning/readiness', { params });
  return data;
}

export async function getSystemDashboardSummary() {
  const { data } = await apiClient.get('/api/system/dashboard/summary');
  return data.data;
}

export async function getCustomerUsers(customerId) {
  const { data } = await apiClient.get(`/api/system/customers/${customerId}/users`);
  return data;
}

export async function inviteCustomerUser(customerId, payload) {
  const { data } = await apiClient.post(`/api/system/customers/${customerId}/users/invite`, payload);
  return data.data;
}

export async function suspendCustomerUser(customerId, userId) {
  const { data } = await apiClient.patch(`/api/system/customers/${customerId}/users/${userId}/suspend`);
  return data.data;
}

export async function activateCustomerUser(customerId, userId) {
  const { data } = await apiClient.patch(`/api/system/customers/${customerId}/users/${userId}/activate`);
  return data.data;
}

export async function removeCustomerUser(customerId, userId) {
  await apiClient.delete(`/api/system/customers/${customerId}/users/${userId}`);
}

export async function getCustomerInvitations(customerId) {
  const { data } = await apiClient.get(`/api/system/customers/${customerId}/invitations`);
  return data;
}

export async function resendCustomerInvitation(invitationId) {
  const { data } = await apiClient.post(`/api/system/customer-invitations/${invitationId}/resend`);
  return data;
}

export async function revokeCustomerInvitation(invitationId) {
  const { data } = await apiClient.post(`/api/system/customer-invitations/${invitationId}/revoke`);
  return data.data;
}

export async function getInvitation(token) {
  const { data } = await apiClient.get(`/api/customer-invitations/${token}`);
  return data.data;
}

export async function acceptInvitation(token, payload) {
  const { data } = await apiClient.post(`/api/customer-invitations/${token}/accept`, payload);
  return data.data;
}

export * from './customerPortalApi';
